import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPlatforms } from '@ionic/react';
import {
  Backdrop,
  BottomSheetContainer,
  BottomSheetContent,
  CloseButton,
  Footer,
  Header,
  ItemHeaderFilter,
  ScrollableContent,
} from '../FilterBottomSheet.styles';
import {
  AllieAccordion,
  AllieAccordionGroup,
  AllieButton,
  AllieText,
} from '../../../../components';
import { AllieCloseOutlineIcon } from '../../../../styling/icons';
import { BottomSheetComponentProps, FilterOption } from '../FilterBottomSheet.types';
import { ChipFilter } from '../FilterTypes/ChipFilter';

export const BottomSheetComponent: FC<BottomSheetComponentProps> = ({
  setIsOpen,
  isOpen,
  labels,
  filters: initialFilters,
  preselectedFilters,
  onApplyFilters,
  onClose,
  onFilterChange,
  showCloseButton = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [filters, setFilters] = useState<FilterOption[]>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<Record<string, string | string[]>>(preselectedFilters);
  const accordionGroup = useRef<null | HTMLIonAccordionGroupElement>(null);

  // Detect iOS platform
  const isIOS = useMemo(() => {
    const platforms = getPlatforms();
    return platforms.includes('ios');
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      return undefined;
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedFilters(preselectedFilters);
  }, [preselectedFilters]);

  useEffect(() => {
    if (isVisible && accordionGroup.current) {
      const timer = setTimeout(() => {
        if (accordionGroup.current) {
          accordionGroup.current.value = filters.map((filter) => filter.filterKey);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible, filters]);

  const handleFilterChange = useCallback(
    (filterName: string, value: string | string[]) => {
      const newSelectedFilters = { ...selectedFilters, [filterName]: value };
      setSelectedFilters(newSelectedFilters);
      onFilterChange?.(filterName, value, { setFilters, setSelectedFilters, filters });
    },
    [selectedFilters, onFilterChange]
  );

  const handleApplyFilters = useCallback(() => {
    const currentFilters = filters.reduce(
      (acc, filter) => {
        const value = selectedFilters[filter.filterKey];
        if (value !== undefined && value !== '') {
          acc[filter.filterKey] = value;
        }
        return acc;
      },
      {} as Record<string, string | string[]>
    );

    onApplyFilters(currentFilters);
    onClose?.();
    setIsOpen(false);
  }, [selectedFilters, onApplyFilters, onClose, setIsOpen]);

  const renderFilter = useCallback(
    (filter: FilterOption) => {
      const filterProps = {
        selectedValues: selectedFilters[filter.filterKey] || '',
        onChange: (value: string | string[]) => handleFilterChange(filter.filterKey, value),
      };

      if (filter.type === 'custom') {
        return filter.renderContent(filterProps);
      }

      return <ChipFilter {...filter} {...filterProps} />;
    },
    [selectedFilters, handleFilterChange]
  );

  const filterItems = useMemo(
    () =>
      filters.map((filter, index) => (
        <AllieAccordion key={index} value={filter.filterKey} disabled={filter.disabled}>
          <ItemHeaderFilter slot="header">
            <AllieText type="body2" weight={500} color={'neutral-70'}>
              {filter.label}
            </AllieText>
          </ItemHeaderFilter>
          <div slot="content">{renderFilter(filter)}</div>
        </AllieAccordion>
      )),
    [filters, renderFilter]
  );

  const modalContent = (
    <>
      {isVisible && <Backdrop onClick={() => setIsOpen(false)} />}
      {isVisible && (
        <BottomSheetContainer isOpen={isOpen}>
          <BottomSheetContent isIOS={isIOS}>
            <Header>
              <AllieText type="h4" weight={600} color={'neutral-90'}>
                {labels.filterBy}
              </AllieText>
              {showCloseButton && (
                <CloseButton allieType="subtle" allieSize="xs" onClick={() => setIsOpen(false)}>
                  <AllieCloseOutlineIcon />
                </CloseButton>
              )}
            </Header>
            <ScrollableContent>
              <AllieAccordionGroup multiple style={{ paddingBottom: '16px' }} ref={accordionGroup}>
                {filterItems}
              </AllieAccordionGroup>
            </ScrollableContent>
            <Footer>
              <AllieButton
                allieType="primary"
                allieSize="small"
                corner="rounded-edges-plus"
                onClick={handleApplyFilters}
                full
              >
                <AllieText type="h3" weight={600} color={'white'}>
                  {labels.apply}
                </AllieText>
              </AllieButton>
            </Footer>
          </BottomSheetContent>
        </BottomSheetContainer>
      )}
    </>
  );

  return createPortal(modalContent, document.body);
};
